import React, { useState, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSetAtom, useAtom } from 'jotai';
import useAPI from '../../../api';
import SectionPage from '../../../Components/Paper/SectionPage';
import LeadOriginsListItem from './Components/LeadOriginsListItem';
import LeadOriginDialog from './Components/LeadOriginDialog';
import { openSnackbarAdviseAtom, searchValuesAtom } from '../../../Atoms';
import RolesFilters from '../../RoleManagement/Components/RolesFilters';

const getLeadOriginListItem = (data, onClick, onDelete) => (
  <LeadOriginsListItem
    key={data.id}
    data={data}
    onClick={onClick}
    onDelete={onDelete}
  />
);

const LeadOriginsPage = function LeadsOriginPage() {
  const intl = useIntl();
  const API = useAPI();

  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);
  const [searchValues, setSearchValues] = useAtom(searchValuesAtom);
  const [leadOriginDialog, setLeadOriginDialog] = useState(null);
  const [leadOrigins, setLeadOrigins] = useState([]);
  const [title, setTitle] = useState('');

  const openNewLeadOriginDialog = () => {
    setTitle(intl.messages.CREATE_LEAD_ORIGIN);
    setLeadOriginDialog(true);
  };

  const openEditLeadOriginDialog = (leadOriginData) => {
    setTitle(`${intl.messages.EDIT_LEAD_ORIGIN}
      ${leadOriginData.leadOrigin}`);
    setLeadOriginDialog(leadOriginData);
  };

  useEffect(() => {
    API.getLeadOrigins(
      {},
      (res) => {
        setLeadOrigins(res);
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.LEAD_ORIGINS_LOADING_ERROR,
          severity: 'error',
        });
      }
    );
  }, []);

  /****/
  const handleSubmit = (data) => {
    if (data.id === 'newLeadOrigin') {
      // eslint-disable-next-line no-unused-vars
      const { id, ...rest } = data;
      API.createLeadOrigin(
        rest,
        (newLeadOrigin) => {
          setOpenSnackbarAdvise({
            message: intl.messages.LEAD_ORIGIN_CREATE_SUCCESSFULLY,
            severity: 'success',
          });
          setLeadOrigins((prev) => [newLeadOrigin, ...prev]);
          setLeadOriginDialog(null);
        },
        (err) => {
          setOpenSnackbarAdvise({
            message: `${intl.messages.ERROR}: ${
              err?.status ? `${err?.status} - ` : ''
            } ${intl.messages.CREATE_LEAD_ORIGIN_ERROR}`,
            severity: 'error',
          });
        }
      );
    } else {
      API.editLeadOrigin(
        data,
        (editLeadOrigin) => {
          setOpenSnackbarAdvise({
            message: intl.messages.LEAD_ORIGIN_EDITED_SUCCESSFULLY,
            severity: 'success',
          });
          setLeadOrigins((prev) =>
            prev.map((item) =>
              item.id === editLeadOrigin.id ? editLeadOrigin : item
            )
          );
          setLeadOriginDialog(null);
        },
        (err) => {
          setOpenSnackbarAdvise({
            message: `${intl.messages.ERROR}: ${
              err?.status ? `${err?.status} - ` : ''
            } ${intl.messages.EDITING_LEAD_ORIGIN_ERROR}`,
            severity: 'error',
          });
        }
      );
    }
  };

  const handleDeleteLeadOrigin = (id) => {
    API.deleteLeadOrigin(
      id,
      () => {
        setLeadOrigins((prevItems) =>
          prevItems.filter((item) => item.id !== id)
        );
        setOpenSnackbarAdvise({
          message: intl.messages.LEAD_ORIGIN_DELETED,
          severity: 'success',
        });
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.LEAD_ORIGIN_DELETE_ERROR,
          severity: 'error',
        });
      }
    );
  };

  const filteredAndSortedLeadOrigins = useMemo(() => {
    if (!leadOrigins?.length) return [];

    if (searchValues?.searchBox) {
      const query = searchValues.searchBox.toUpperCase();
      return leadOrigins.filter(({ leadOrigin }) =>
        leadOrigin?.toUpperCase().includes(query)
      );
    }

    return leadOrigins;
  }, [searchValues, leadOrigins]);

  return (
    <>
      <SectionPage
        hideExportExcel
        hideFilters
        hideSorting
        sectionName="LEAD_ORIGINS"
        sectionSubName="LEADS"
        fab={intl.messages.CREATE_LEAD_ORIGIN}
        onClickFab={openNewLeadOriginDialog}
        fabVariant="extended"
        customEntryFunction={(item) =>
          getLeadOriginListItem(
            item,
            openEditLeadOriginDialog,
            handleDeleteLeadOrigin
          )
        }
        singlePageList={filteredAndSortedLeadOrigins}
        customFilters={
          <RolesFilters
            defaultValues={{
              searchBox: searchValues?.searchBox || '',
            }}
            onChangeFilters={(newSearchValues) => {
              setSearchValues((prev) => ({
                ...prev,
                ...newSearchValues,
              }));
            }}
          />
        }
      />
      <LeadOriginDialog
        title={title}
        onSubmit={handleSubmit}
        open={Boolean(leadOriginDialog)}
        leadOriginData={leadOriginDialog}
        edit={leadOriginDialog?.dialogType === 'EDIT'}
        close={() => {
          setLeadOriginDialog(null);
        }}
      />
    </>
  );
};
export default LeadOriginsPage;
